import Api from '../services/Api';
import { NotaFiscalList } from '../shared/types/notaFiscalList';

// [AJUSTE 12/08/2026] Transforma a falha em frase, cobrindo os casos que antes ficavam
// mudos. O `...error.response.data` sozinho nao bastava:
//
//   - erro de REDE/timeout: nao existe `response`, entao nada era espalhado e a tela
//     recebia apenas { success: false }, sem uma palavra sobre o motivo;
//   - 504 do NGINX: `data` e a pagina HTML dele (uma string). Espalhar string vira
//     { 0: '<', 1: 'h', ... } - lixo, e mensagem nenhuma.
//
// Ordem de preferencia: o que a API disser (ErroDetalhado/Erro), depois o caso
// conhecido pelo status, e por fim uma frase generica que ao menos nao mente.
function mensagemDeFalha(error: any): string {
  const data = error?.response?.data;

  if (data && typeof data === 'object') {
    const doServidor = data.ErroDetalhado || data.Erro || data.error?.message;
    if (doServidor) return doServidor;
  }

  const status = error?.response?.status;

  if (status === 503) {
    return 'O serviço de nota fiscal não respondeu. A nota NÃO foi enviada e continua como não transmitida.';
  }

  if (status === 504 || status === 502) {
    return 'O serviço de nota fiscal não respondeu no tempo esperado. A nota NÃO foi enviada — tente novamente em alguns minutos.';
  }

  if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
    return 'A requisição demorou demais e foi interrompida. A nota pode não ter sido enviada — confira o status antes de tentar de novo.';
  }

  if (!error?.response) {
    return 'Não foi possível falar com o servidor. Verifique a conexão e tente novamente.';
  }

  return 'Houve algum erro ao transmitir a nota. Verifique os dados!';
}

class NotaFiscalController {
  public static async listarNotasFiscais(
    request?: NotaFiscalList
  ): Promise<any> {
    try {
      const response = await Api.get(`api/NotaFiscal/Listar`, {
        params: request,
      });
      return response?.data ?? [];
    } catch (error: any) {
      return [];
    }
  }

  public static async transmitirNotaFiscal(nfeId: number): Promise<any> {
    try {
      const response = await Api.post(
        `api/NotaFiscal/TransmitirNotaFiscal/${nfeId}`
      );
      return {
        success: true,
        response,
      };
    } catch (error: any) {
      return {
        success: false,
        mensagem: mensagemDeFalha(error),
        ...(typeof error?.response?.data === 'object' ? error.response.data : {}),
      };
    }
  }

  // Consulta a nota no servico de NF-e e reaplica o retorno. Serve para quando o
  // webhook do servico se perde e a nota fica presa em "Processando" mesmo tendo
  // sido autorizada - antes disso, a unica saida era acerto manual no banco.
  public static async sincronizarStatus(nfeId: number): Promise<any> {
    try {
      const response = await Api.post(
        `api/NotaFiscal/SincronizarStatus/${nfeId}`
      );
      return {
        success: true,
        response,
      };
    } catch (error: any) {
      return {
        success: false,
        ...error?.response?.data,
      };
    }
  }

  /**
   * Baixa um documento da nota.
   *
   * [08/10] `origem` diz QUAL documento: 'NotaFiscal' (a DANFE), 'CartaCorrecao' ou
   * 'Cancelamento'. Sem ela, o código não distingue — carta de correção e cancelamento
   * são os dois 135, e uma nota que tem os dois devolveria qualquer um.
   *
   * Parâmetro vazio é OMITIDO da URL, nunca mandado em branco: a API converte só `null`
   * em "sem filtro"; string vazia chega na procedure como `= ''` e não casa com nada.
   */
  public static async getNotaFiscalArquivo(
    nfeId: number,
    tipo: string,
    statusCode?: string,
    origem?: string
  ): Promise<any> {
    try {
      const params = new URLSearchParams({ tipo });
      if (statusCode) params.append('statusCode', statusCode);
      if (origem) params.append('origem', origem);

      const response = await Api.get(
        `api/NotaFiscal/GetNotaFiscalArquivo/${nfeId}?${params.toString()}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error: any) {
      // [08/10] O 404 desta rota responde uma STRING, não um objeto. Espalhar uma string
      // com `...` vira {0:'A',1:' ',...} e a mensagem some — a tela caía no texto genérico
      // "Verifique os dados!" justamente quando a API tinha explicado o que faltava.
      const corpo = error?.response?.data;
      if (typeof corpo === 'string') {
        return { success: false, message: corpo };
      }

      return {
        success: false,
        ...corpo,
      };
    }
  }

  /**
   * Exclui uma nota que não foi transmitida.
   *
   * Diferente de cancelar: cancelamento é evento na SEFAZ, para nota autorizada. Excluir é
   * para nota que nunca chegou lá — some da listagem e a venda volta a poder ser faturada.
   *
   * O 400 é recusa de regra, com o motivo pronto para o operador ler.
   */
  public static async excluirNotaFiscal(nfeId: number): Promise<any> {
    try {
      const response = await Api.post(`api/NotaFiscal/ExcluirNotaFiscal/${nfeId}`, {});

      // { success, dados } e a forma que ESTE controller usa - o Response de
      // { StatusCode, Data } e de outros controllers. Seguir o do arquivo evita que quem
      // leia os dois tenha de lembrar qual e qual.
      return { success: true, dados: response?.data };
    } catch (error: any) {
      return {
        success: false,
        mensagem: error?.response?.data?.ErroDetalhado
               ?? error?.response?.data?.Erro
               ?? 'Não foi possível excluir a nota fiscal.',
      };
    }
  }

  public static async cancelarNotaFiscal(
    nfeId: number,
    justificativa: string
  ): Promise<any> {
    try {
      const response = await Api.post(`api/NotaFiscal/Cancelar/${nfeId}`, {
        justificativa,
      });
      return {
        success: true,
        response,
      };
    } catch (error: any) {
      return {
        success: false,
        ...error?.response?.data,
      };
    }
  }

  public static async cartaCorrecaoNotaFiscal(
    nfeId: number,
    justificativa: string
  ): Promise<any> {
    try {
      const response = await Api.post(`api/NotaFiscal/CartaCorrecao/${nfeId}`, {
        justificativa,
      });
      return {
        success: true,
        response,
      };
    } catch (error: any) {
      return {
        success: false,
        ...error?.response?.data,
      };
    }
  }
}

export default NotaFiscalController;
